import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { NotificationPreferences } from "@/contexts/AuthContext";
import { isValidLanguageCode, setStoredLanguage } from "@/lib/languages";

export interface UpdateProfileInput {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  language?: string | null;
  avatar_url?: string | null;
  phone?: string | null;
  timezone?: string | null;
  notification_preferences?: NotificationPreferences | null;
}

export function useUpdateProfile() {
  const { user, refetchProfile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateProfileInput) => {
      if (!user?.id) throw new Error("Not signed in");
      if (input.language && isValidLanguageCode(input.language)) {
        setStoredLanguage(input.language);
      }
      const { error } = await supabase
        .from("profiles")
        .update(input)
        .eq("id", user.id);
      if (error) throw error;
      return input;
    },
    onSuccess: () => {
      refetchProfile();
      queryClient.invalidateQueries({ queryKey: ["profiles-assignable-avatars"] });
      queryClient.invalidateQueries({ queryKey: ["property-access-and-invites"] });
      queryClient.invalidateQueries({ queryKey: ["property-cohost-access"] });
    },
  });
}
